import { useEffect, useRef } from 'react';
import './ErrorBoundary.css';

export interface ErrorUIProps {
  errorType: 'network' | 'api' | 'asset_load' | 'quality' | 'timeout' | 'quota' | 'javascript';
  errorMessage?: string;
  onPlayDemo: () => Promise<void>;
}

const ErrorUI = ({ onPlayDemo }: ErrorUIProps) => {
  const playDemoButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus Play Demo button on mount
  useEffect(() => {
    playDemoButtonRef.current?.focus();
  }, []);

  // Escape key handler: Activates demo mode (same as clicking Play Demo)
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Story 6.2 Code Review Fix: Add error handling for async call
        onPlayDemo().catch((err) => {
          console.error('Demo mode activation failed:', err);
        });
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onPlayDemo]);

  return (
    <div className="error-ui-container error-boundary-container" role="alert" aria-live="assertive">
      {/* Story 6.2 AC4: Screen reader announcement - visually hidden but announced */}
      <div className="sr-only" aria-live="assertive">
        Alert: Generation failed. Playing with demo assets. Press Play Demo button to start.
      </div>

      <div className="error-boundary-content">
        <h1 className="error-heading">Oops! Something went wrong</h1>
        <p className="error-message">
          Don't worry, you can still play with our demo assets!
        </p>

        <button
          ref={playDemoButtonRef}
          className="refresh-button"
          onClick={onPlayDemo}
          aria-label="Play demo game"
        >
          {/* Play arrow icon SVG - 20px */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
          Play Demo
        </button>
      </div>
    </div>
  );
};

export default ErrorUI;
